import React, { useState, useEffect, useRef } from 'react';
import { Shield, Star, Zap, Award, BadgeCheck, Info, ChevronDown, ChevronUp, Sparkles, Trophy } from 'lucide-react';
import axios from '../lib/axios';
import { API } from '../App';

// Trust tier definitions with clear descriptions
const TRUST_TIERS = {
  'New': { 
    color: '#94a3b8', 
    icon: Sparkles,
    description: 'New to the platform',
    range: '0-20%'
  },
  'Bronze': { 
    color: '#cd7f32', 
    icon: Shield,
    description: 'Building reputation',
    range: '21-40%'
  },
  'Silver': { 
    color: '#9ca3af', 
    icon: Star,
    description: 'Established seller',
    range: '41-60%'
  },
  'Gold': { 
    color: '#f59e0b', 
    icon: Award,
    description: 'Trusted seller',
    range: '61-80%'
  },
  'Platinum': { 
    color: '#06b6d4', 
    icon: Trophy,
    description: 'Top-rated seller',
    range: '81-100%'
  }
};

const TrustScoreBadge = ({ userId, size = 'default', showDetails = false }) => {
  const [trustData, setTrustData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (userId) {
      fetchTrustScore();
    }
  }, [userId]);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showTooltip]);

  const fetchTrustScore = async () => {
    try {
      const response = await axios.get(`${API}/smart/trust-score/${userId}`);
      setTrustData(response.data);
    } catch (error) {
      console.error('Failed to fetch trust score:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`animate-pulse bg-secondary rounded-full ${size === 'sm' || size === 'small' ? 'w-20 h-5' : 'w-24 h-6'}`} />
    );
  }

  if (!trustData) return null;

  const { score, percentage, tier, tier_color, factors, badges } = trustData;
  const tierInfo = TRUST_TIERS[tier] || TRUST_TIERS['Bronze'];
  const TierIcon = tierInfo.icon;

  // Extra small - compact inline with tooltip
  if (size === 'sm') {
    return (
      <div className="relative inline-block" ref={tooltipRef}>
        <button
          onClick={() => setShowTooltip(!showTooltip)}
          className="inline-flex items-center gap-1.5 text-xs hover:opacity-80 transition-opacity"
        >
          <TierIcon className="w-3.5 h-3.5" style={{ color: tier_color }} />
          <span className="font-semibold" style={{ color: tier_color }}>
            {percentage}% {tier}
          </span>
        </button>
        
        {showTooltip && (
          <div className="absolute bottom-full left-0 mb-2 p-3 bg-card border border-border rounded-xl shadow-xl z-[60] min-w-[220px]">
            {/* Header */}
            <div className="flex items-center gap-2 mb-2 pb-2 border-b border-border">
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${tier_color}20` }}
              >
                <TierIcon className="w-4 h-4" style={{ color: tier_color }} />
              </div>
              <div>
                <div className="font-bold text-sm" style={{ color: tier_color }}>
                  {tier} Seller
                </div>
                <div className="text-xs text-muted-foreground">{tierInfo.description}</div>
              </div>
            </div>
            
            {/* Score */}
            <div className="mb-2">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-muted-foreground">Trust Score</span>
                <span className="font-bold" style={{ color: tier_color }}>{percentage}%</span>
              </div>
              <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full"
                  style={{ width: `${percentage}%`, backgroundColor: tier_color }}
                />
              </div>
            </div>
            
            {/* Tier levels */}
            <div className="text-[10px] text-muted-foreground space-y-0.5">
              <p className="font-medium text-foreground mb-1">Trust Levels:</p>
              {Object.entries(TRUST_TIERS).map(([name, info]) => (
                <div key={name} className={`flex items-center gap-1.5 ${tier === name ? 'text-foreground font-medium' : ''}`}>
                  <span style={{ color: info.color }}>●</span>
                  <span>{name}</span>
                  <span className="opacity-60">({info.range})</span>
                </div>
              ))}
            </div>
            
            {/* What it's based on */}
            <p className="text-[10px] text-muted-foreground mt-2 pt-2 border-t border-border">
              Based on: profile verification, response time, completed transactions & reviews
            </p>
          </div>
        )}
      </div>
    );
  }

  // Small badge for listing cards (desktop sidebar)
  if (size === 'small') {
    return (
      <div className="relative inline-block" ref={tooltipRef}>
        <button
          onClick={() => setShowTooltip(!showTooltip)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold cursor-pointer hover:opacity-80"
          style={{ backgroundColor: `${tier_color}15`, color: tier_color }}
        >
          <TierIcon className="w-3.5 h-3.5" />
          <span>{percentage}% {tier}</span>
        </button>
        
        {showTooltip && (
          <div className="absolute top-full left-0 mt-2 p-3 bg-card border border-border rounded-xl shadow-xl z-[60] min-w-[240px]">
            {/* Header */}
            <div className="flex items-center gap-2 mb-2 pb-2 border-b border-border">
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${tier_color}20` }}
              >
                <TierIcon className="w-4 h-4" style={{ color: tier_color }} />
              </div>
              <div>
                <div className="font-bold text-sm" style={{ color: tier_color }}>
                  {tier} Seller
                </div>
                <div className="text-xs text-muted-foreground">{tierInfo.description}</div>
              </div>
            </div>
            
            {/* Score */}
            <div className="mb-2">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-muted-foreground">Trust Score</span>
                <span className="font-bold" style={{ color: tier_color }}>{percentage}%</span>
              </div>
              <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full"
                  style={{ width: `${percentage}%`, backgroundColor: tier_color }}
                />
              </div>
            </div>
            
            {/* Tier levels */}
            <div className="text-[10px] text-muted-foreground space-y-0.5">
              <p className="font-medium text-foreground mb-1">Trust Levels:</p>
              {Object.entries(TRUST_TIERS).map(([name, info]) => (
                <div key={name} className={`flex items-center gap-1.5 ${tier === name ? 'text-foreground font-medium' : ''}`}>
                  <span style={{ color: info.color }}>●</span>
                  <span>{name}</span>
                  <span className="opacity-60">({info.range})</span>
                </div>
              ))}
            </div>
            
            {/* What it's based on */}
            <p className="text-[10px] text-muted-foreground mt-2 pt-2 border-t border-border">
              Based on: profile verification, response time, completed transactions & reviews
            </p>
          </div>
        )}
      </div>
    );
  }

  // Medium badge - for desktop sidebar
  if (size === 'medium') {
    return (
      <div className="space-y-2">
        <div 
          className="flex items-center gap-2 px-3 py-2 rounded-xl"
          style={{ backgroundColor: `${tier_color}10`, border: `1px solid ${tier_color}30` }}
        >
          <div 
            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${tier_color}20` }}
          >
            <TierIcon className="w-5 h-5" style={{ color: tier_color }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm" style={{ color: tier_color }}>{tier} Seller</span>
              <span className="text-xs text-muted-foreground">({percentage}%)</span>
            </div>
            <p className="text-xs text-muted-foreground">{tierInfo.description}</p>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="px-1">
          <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full"
              style={{ width: `${percentage}%`, backgroundColor: tier_color }}
            />
          </div>
          <div className="flex justify-between mt-1 text-[10px] text-muted-foreground">
            <span>New</span>
            <span>Platinum</span>
          </div>
        </div>
      </div>
    );
  }

  // Full trust score card (default)
  return (
    <div className="bg-card rounded-2xl border border-border overflow-hidden">
      {/* Header */}
      <div 
        className="p-4 cursor-pointer"
        onClick={() => showDetails && setExpanded(!expanded)}
        style={{ background: `linear-gradient(135deg, ${tier_color}10, transparent)` }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="w-14 h-14 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${tier_color}20` }}
            >
              <TierIcon className="w-7 h-7" style={{ color: tier_color }} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold text-foreground">{percentage}%</span>
                <span 
                  className="px-2.5 py-1 rounded-full text-xs font-bold"
                  style={{ backgroundColor: tier_color, color: 'white' }}
                >
                  {tier}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">{tierInfo.description}</p>
            </div>
          </div>
          
          {showDetails && (
            <button className="p-2 text-muted-foreground hover:text-foreground transition-colors">
              {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          )}
        </div>

        {/* Progress bar */}
        <div className="mt-4">
          <div className="h-2 bg-secondary rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full"
              style={{ width: `${percentage}%`, backgroundColor: tier_color }}
            />
          </div>
          <div className="flex justify-between mt-1.5 text-xs text-muted-foreground">
            {Object.entries(TRUST_TIERS).map(([name, info]) => (
              <span 
                key={name} 
                className={tier === name ? 'font-bold' : 'opacity-60'}
                style={{ color: tier === name ? info.color : undefined }}
              >
                {name}
              </span>
            ))}
          </div>
        </div>

        {/* Badges */}
        {badges && badges.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {badges.map((badge, i) => (
              <div 
                key={i}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium"
                style={{ backgroundColor: `${badge.color}20`, color: badge.color }}
              >
                {badge.icon === 'badge-check' && <BadgeCheck className="w-3.5 h-3.5" />}
                {badge.icon === 'zap' && <Zap className="w-3.5 h-3.5" />}
                {badge.icon === 'star' && <Star className="w-3.5 h-3.5" />}
                {badge.icon === 'shield-check' && <Shield className="w-3.5 h-3.5" />}
                {badge.icon === 'award' && <Award className="w-3.5 h-3.5" />}
                {badge.name}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Expanded details */}
      {showDetails && expanded && factors && (
        <div className="p-4 pt-0 border-t border-border mt-4">
          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Info className="w-4 h-4" />
            Score Breakdown
          </h4>
          <div className="space-y-3">
            {factors.map((factor, i) => (
              <div key={i}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-muted-foreground">{factor.name}</span>
                  <span className="text-foreground font-medium">{factor.score}/{factor.max}</span>
                </div>
                <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full"
                    style={{ 
                      width: `${(factor.score / factor.max) * 100}%`,
                      backgroundColor: factor.score === factor.max ? '#10b981' : tier_color
                    }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">{factor.detail}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TrustScoreBadge;
